import React, { useState, useRef, useEffect } from 'react';
import { CiSearch } from 'react-icons/ci';
import { FiFilter } from 'react-icons/fi';
import { IoClose } from 'react-icons/io5';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { FaSearch, FaStar } from 'react-icons/fa';
import { Filters } from '../types/filter';

const PriceRangeSlider = ({ onChange }: { onChange: (value: Filters) => void }) => {
    const [range, setRange] = useState<[number, number]>([0, 1000]);
    const [rate, setRate] = useState<number>(0);
    const [orderRange, setOrderRange] = useState<number>(0);
    const handleApplyFilters = () => {
        onChange({ price: range, rate: rate, numOfOrders: orderRange });
    };

    return (
        <div>
            <div className="mt-4">
                <h3 className="text-md font-medium mb-2">نطاق السعر</h3>
                <Slider range min={0} max={1000} defaultValue={range} onChange={(value) => Array.isArray(value) && setRange(value as [number, number])} />
                <div className="flex justify-between mt-2">
                    <span>{range[0]}EGP</span>
                    <span>{range[1]}EGP</span>
                </div>
            </div>

            <div className="mt-4">
                <h3 className="text-md font-medium mb-2">نطاق التقييم</h3>
                <Slider min={0} max={5} defaultValue={rate} onChange={(value) => setRate(value as number)} />
                <div className="flex justify-between mt-2">
                    <span className='flex items-center'>{rate} <FaStar className="text-yellow-300 ml-2" /></span>
                </div>
            </div>

            <div className="mt-4">
                <h3 className="text-md font-medium mb-2">عدد الطلبات</h3>
                <Slider min={0} max={50} defaultValue={orderRange} onChange={(value) => setOrderRange(value as number)} />
                <div className="flex justify-between mt-2">
                    <span>{orderRange}</span>
                </div>
            </div>

            {/* Apply Filters Button */}
            <button className="w-full mt-4 bg-blue-500 text-white py-2 rounded-md hover:bg-blue-600 transition" onClick={handleApplyFilters}>
                تطبيق الفلاتر
            </button>
        </div>
    );
};

const FilterSidebar = ({ onClose, onApplyFilters }: { onClose: () => void, onApplyFilters: (filters: Filters) => void }) => {
    const sidebarRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-40 z-10" onClick={onClose}></div>
            <div ref={sidebarRef} className="fixed top-0 right-0 h-full w-full sm:w-64 bg-white shadow-lg p-4 z-20 transition-transform">
                <div className="flex justify-between items-center">
                    <h2 className="text-lg font-semibold">الفلاتر</h2>
                    <IoClose size={24} className="cursor-pointer text-gray-600 hover:text-red-500" onClick={onClose} />
                </div>

                <div className="space-y-2">
                    <PriceRangeSlider onChange={onApplyFilters} />
                </div>
            </div>
        </>
    );
};

const SearchBarWithFilters = ({ onChange, filter }: { onChange: (value: Filters) => void, filter?: Filters }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [filters, setFilters] = useState<Filters>(() => filter || { price: [0, 1000], rate: 0, numOfOrders: 0 });


    useEffect(() => {
        if (filter) {
            setFilters(filter);
        }
    }, [filter]);
    const searchBarRef = useRef<HTMLDivElement>(null);

    const handleSearchToggle = () => {
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchBarRef.current && !searchBarRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setIsOpen(false);
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const handleApplyFilters = (appliedFilters: Filters) => {
        setFilters(appliedFilters);
        onChange(appliedFilters);

        setIsSidebarOpen(false);
    };

    const handleSearch = () => {
        onChange({ ...filters, name: searchQuery });
        setIsSidebarOpen(false);
        setIsOpen(false)
    };

    return (
        <div className="relative" ref={searchBarRef}>
            <div className="group relative cursor-pointer" onClick={handleSearchToggle}>
                {/* Outline Icon (Default) */}
                <CiSearch
                    size={27}
                    className="text-black group-hover:hidden transition-all duration-200"
                />

                {/* Filled Icon (Shown on Hover) */}
                <FaSearch
                    size={27}
                    className="text-primary-200 hidden group-hover:block transition-all duration-200 "
                />
            </div>

            {isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-30 z-10 md:hidden" onClick={() => setIsOpen(false)} />
            )}

            <div className={`fixed md:absolute bg-white shadow-lg rounded-lg px-3 py-2 flex items-center space-x-2 transition-all duration-300 z-20
                ${isOpen ? 'opacity-100' : 'opacity-0 -translate-y-5'}
                top-16 left-1/2 transform -translate-x-1/2 w-full md:top-0 md:left-10 md:translate-x-0 md:w-96
                `}
            >
                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="ابحث هنا..." className="flex-grow outline-none text-sm placeholder-gray-400 pr-6" />

                <FiFilter size={18} className="absolute right-16 cursor-pointer text-gray-500 hover:text-primary-500 transition" onClick={() => setIsSidebarOpen(true)} />
                <button onClick={handleSearch} className="bg-primary-200 text-white px-2 py-1 rounded-md hover:bg-primary-300 transition">
                    بحث
                </button>
            </div>

            {isSidebarOpen && <FilterSidebar onClose={() => setIsSidebarOpen(false)} onApplyFilters={handleApplyFilters} />}
        </div>
    );
};

export default SearchBarWithFilters;